import React, { useState, useEffect } from 'react'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter } from '@dnd-kit/core'
import { TrainingSprintStepProps, Participant, Team } from '../types'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { AddSprintParticipant } from '../components/AddSprintParticipant/AddSprintParticipant'
import { CopyFromPreviousSprint } from '../components/CopyFromPreviousSprint/CopyFromPreviousSprint'
import { ParticipantsContainer } from '../components/ParticipantsContainer/ParticipantsContainer'
import { ParticipantBoxInfo } from '../components/ParticipantBoxInfo/ParticipantBoxInfo'
import { Sprint } from '../../../../../../pure-js/types/Sprint'
import { generateTeamName } from '../utils'
import { useOrgTenant } from '../../../organization/OrgContext'
import { useOrgMembers } from '../../../../apiLayer/queryHooks/queries'
import useAppState from '../../../../hooks/useAppState'
import { isParticipantOrgRole } from 'pure-js/libs/organizationMembershipHelpers'
import type { OrgMemberOption } from '../components/AddSprintParticipant/AddSprintParticipant'

export const TrainingSprintParticipantsStepThree: React.FC<TrainingSprintStepProps> = ({
  data,
  updateData,
  stepId: _stepId,
  isActive: _isActive,
  isCompleted: _isCompleted,
  isVisited: _isVisited,
  hasValidationErrors = false,
  validationErrors = []
}) => {
  const { t } = useTranslation()
  const { state } = useAppState()
  const currentUserId = state.user?.id
  const { activeMembership } = useOrgTenant()
  const { data: orgMembersRes } = useOrgMembers(activeMembership?.organizationId)
  // Only participant roles can be added as participants; exclude admins/facilitators and self.
  const orgMembers: OrgMemberOption[] = (orgMembersRes?.data ?? [])
    .filter((member) => member.email && isParticipantOrgRole(member.role) && member.userId !== currentUserId)
    .map((m) => ({ userId: m.userId, name: m.displayName || m.email || '', email: m.email || '' }))
  const [participants, setParticipants] = useState<Participant[]>(data.participants || [])
  const [teams, setTeams] = useState<Team[]>(data.teams || [])
  const [activeParticipant, setActiveParticipant] = useState<Participant | null>(null)

  // Sync local state with parent data when it changes
  useEffect(() => {
    setParticipants(data.participants || [])
    setTeams(data.teams || [])
  }, [data.participants, data.teams])

  const handleAddParticipant = (newParticipant: { name: string; email: string }) => {
    const participant: Participant = {
      id: `participant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: newParticipant.name,
      email: newParticipant.email
    }

    const updatedParticipants = [...participants, participant]

    setParticipants(updatedParticipants)
    updateData('participants', updatedParticipants)
  }

  const handleCopyFromPreviousSprint = (sprint: Sprint) => {
    const newParticipants_ = sprint.participants.filter(
      (p) => participants.findIndex((existing) => existing.email === p.email) === -1
    )
    const newTeams_ = sprint.teams.filter((t) => teams.findIndex((existing) => existing.name === t.name) === -1)

    const newParticipants = sprint.participants
      .filter((p) => p.email)
      .map((p) => ({
        id: p.userId || `participant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: p.name,
        email: p.email
      })) as Participant[]

    const newTeams: Team[] = newTeams_.map((team) => {
      const teamParticipants = newParticipants_.filter((p) => p.teamId === team.teamId || p.teamId === team.tempTeamId)
      return {
        id: `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: team.name,
        participants: teamParticipants.map((p) => {
          const participant = newParticipants.find((np) => np.name === p.name && np.email === p.email) as Participant
          return (
            participant || {
              id: p.userId || `participant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name: p.name,
              email: p.email as string
            }
          )
        }),
        maxParticipants: 4,
        language: team.language
      }
    })

    const allParticipants = [...participants, ...newParticipants]

    const allTeams = [...teams, ...newTeams]

    setParticipants(allParticipants)
    setTeams(allTeams)
    updateData('participants', allParticipants)
    updateData('teams', allTeams)
  }

  const handleAddTeam = () => {
    const newTeam: Team = {
      id: `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: generateTeamName(teams),
      participants: [],
      maxParticipants: 4
    }

    const updatedTeams = [...teams, newTeam]

    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  const handleRemoveTeam = (teamId: string) => {
    const updatedTeams = teams.filter((team) => team.id !== teamId)

    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  const handleRemoveParticipant = (participantId: string) => {
    // Remove from participants list
    const updatedParticipants = participants.filter((p) => p.id !== participantId)
    setParticipants(updatedParticipants)
    updateData('participants', updatedParticipants)

    // Remove from all teams
    const updatedTeams = teams.map((team) => ({
      ...team,
      participants: team.participants.filter((p) => p.id !== participantId)
    }))
    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  const handleUpdateTeamLanguage = (teamId: string, language: string) => {
    const updatedTeams = teams.map((team) => (team.id === teamId ? { ...team, language } : team))
    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  const handleApplyLanguageToAll = (language: string) => {
    const updatedTeams = teams.map((team) => ({ ...team, language }))
    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    if (active.data.current?.type === 'participant') {
      setActiveParticipant(active.data.current.participant)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveParticipant(null)

    if (!over || !active.data.current?.participant) return

    const participant = active.data.current.participant as Participant
    const participantId = participant.id

    // Handle drop to unassigned
    if (over.id === 'unassigned-participants') {
      handleDropToUnassigned(participantId)
      return
    }

    // Handle drop to team
    if (over.data.current?.type === 'team') {
      const teamId = over.data.current.teamId as string
      handleDropToTeam(participantId, teamId)
      return
    }
  }

  const handleDropToUnassigned = (participantId: string) => {
    // Remove participant from all teams
    const updatedTeams = teams.map((team) => ({
      ...team,
      participants: team.participants.filter((p) => p.id !== participantId)
    }))
    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  const handleDropToTeam = (participantId: string, targetTeamId: string) => {
    // Remove participant from all teams first
    const teamsWithoutParticipant = teams.map((team) => ({
      ...team,
      participants: team.participants.filter((p) => p.id !== participantId)
    }))

    // Add participant to target team if there's space
    const updatedTeams = teamsWithoutParticipant.map((team) => {
      if (team.id === targetTeamId && team.participants.length < team.maxParticipants) {
        const participant = participants.find((p) => p.id === participantId)
        if (participant) {
          return {
            ...team,
            participants: [...team.participants, participant]
          }
        }
      }
      return team
    })

    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  // Auto-assign functionality
  const handleAutoAssign = () => {
    const teamParticipantIds = new Set(teams.flatMap((team) => team.participants.map((p) => p.id)))
    const unassignedParticipants = participants.filter((p) => !teamParticipantIds.has(p.id))

    if (unassignedParticipants.length === 0) return

    // Distribute unassigned participants across teams with available space
    const updatedTeams = [...teams]
    let participantIndex = 0

    // First, fill existing teams
    for (const team of updatedTeams) {
      while (team.participants.length < team.maxParticipants && participantIndex < unassignedParticipants.length) {
        team.participants.push(unassignedParticipants[participantIndex])
        participantIndex++
      }
    }

    // If there are still unassigned participants, create new teams
    const remainingUnassigned = unassignedParticipants.slice(participantIndex)
    const teamSize = 4 // Default team size

    for (let i = 0; i < remainingUnassigned.length; i += teamSize) {
      const teamParticipants = remainingUnassigned.slice(i, i + teamSize)
      const newTeam: Team = {
        id: `auto-team-${Date.now()}-${i}`,
        name: generateTeamName(updatedTeams),
        participants: teamParticipants,
        maxParticipants: teamSize
      }
      updatedTeams.push(newTeam)
    }

    setTeams(updatedTeams)
    updateData('teams', updatedTeams)
  }

  // Get unassigned participants count
  const teamParticipantIds = new Set(teams.flatMap((team) => team.participants.map((p) => p.id)))
  const unassignedCount = participants.filter((p) => !teamParticipantIds.has(p.id)).length

  // Check for validation errors
  const hasUnassignedParticipants = unassignedCount > 0
  const hasMissingTeamLanguages = validationErrors.includes(
    'facilitator.trainingSprint.validation.teamLanguageRequired'
  )

  return (
    <div className="space-y-8">
      <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* Header */}
        <div className="text-center">
          <h2
            className="font-bold mb-4"
            style={{
              color: INTERAX_COLORS.typographyText,
              fontFamily: INTERAX_TYPOGRAPHY.headline2.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.headline2.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.headline2.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.headline2.lineHeight
            }}
          >
            {t('facilitator.trainingSprint.addParticipants.title')}
          </h2>
          <p
            className="text-lg"
            style={{
              color: INTERAX_COLORS.typographyFont,
              fontFamily: INTERAX_TYPOGRAPHY.body1.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body1.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body1.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body1.lineHeight
            }}
          >
            {t('facilitator.trainingSprint.addParticipants.subtitle')}
          </p>
        </div>

        {/* First row - Add participants and Copy from previous sprint */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AddSprintParticipant
            onAddParticipant={handleAddParticipant}
            orgMembers={orgMembers}
            existingEmails={participants.map((p) => p.email).filter(Boolean) as string[]}
          />
          <CopyFromPreviousSprint sprintData={data} onCopyParticipants={handleCopyFromPreviousSprint} />
        </div>

        {/* Participants and Teams container */}
        <ParticipantsContainer
          participants={participants}
          teams={teams}
          mode="creation"
          onAddTeam={handleAddTeam}
          onRemoveTeam={handleRemoveTeam}
          onRemoveParticipant={handleRemoveParticipant}
          onUpdateTeamLanguage={handleUpdateTeamLanguage}
          onApplyLanguageToAll={handleApplyLanguageToAll}
          onDropParticipant={handleDropToUnassigned}
          onDropToTeam={handleDropToTeam}
          onAutoAssign={handleAutoAssign}
          unassignedCount={unassignedCount}
          hasUnassignedParticipants={hasUnassignedParticipants}
          hasValidationErrors={hasValidationErrors}
          hasMissingTeamLanguages={hasMissingTeamLanguages}
        />

        {/* Drag overlay - moved outside the main content */}
        <DragOverlay>
          {activeParticipant ? (
            <div className="transform rotate-3 scale-105">
              <ParticipantBoxInfo
                participant={activeParticipant}
                onRemove={() => {}}
                showRemoveButton={false}
                className="opacity-90 shadow-2xl"
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
